import Head from "next/head";
import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import Link from "next/link";
import Image from "next/image";
import NewHeader from "@/components/NewHeader";
import { OrganizationSchema, LocalBusinessSchema } from "@/components/SEOSchema";
import { SITE_URL } from "@/lib/constants";

// Logo data for "Trusted by" section
const trustedLogos = [
  { name: "City Storage Systems", logo: "/logos/city-storage-systems.svg", width: 140, height: 50 },
  { name: "GK Horizons", logo: "/logos/gk-horizons.svg", width: 150, height: 40 },
  { name: "ShopBack", logo: "/logos/shopback.svg", width: 160, height: 40 },
  { name: "WLM Accounting", logo: "/logos/wlm-accounting.svg", width: 180, height: 40 },
  { name: "Umawar Investment Group", logo: "/logos/umawar.svg", width: 170, height: 40 },
];

// Backed by (VC investors)
const backedByLogos = [
  { name: "Investible", logo: "/logos/investible.svg", width: 140, height: 40, displayHeight: 52 },
  { name: "Skystar Capital", logo: "/logos/skystar-capital.svg", width: 150, height: 40 },
  { name: "Incubate Fund Asia", logo: "/logos/incubate-fund-asia.svg", width: 160, height: 40 },
  { name: "Init-6", logo: "/logos/init-6.svg", width: 120, height: 40, displayHeight: 52 },
];

// Testimonials data
const testimonials = [
  {
    name: "Charlie Tarchichi",
    role: "Associate Director, WLM",
    image: "/team/charlie.svg",
    platform: "Shopify",
    platformIcon: "🛒",
    quote:
      'The AI handles all the grunt work — categorising many transactions daily — but knowing a real accountant reviews everything before BAS time gives us total peace of mind.',
  },
  {
    name: "Michael McGee",
    role: "Managing Partner, McGee & Partners",
    image: "/team/michael.svg",
    platform: "Xero",
    platformIcon: "🔵",
    quote:
      'We tried pure AI tools — they were fast but kept making errors. We tried outsourced accountants — too slow and expensive. Ledgerowl is the best of both.',
  },
  {
    name: "Aaron Ng",
    role: "Principal Consultant, 9 Space",
    image: "/team/aaron.svg",
    platform: "Stripe",
    platformIcon: "💳",
    quote:
      'I can actually call my accountant Sarah and she knows our business. But behind the scenes the AI is doing reconciliation that would take her days.',
  },
];

// Integration logos
const integrationLogos = [
  { name: "Xero", logo: "/logos/xero.svg", width: 80, height: 32 },
  { name: "MYOB", logo: "/logos/myob.svg", width: 80, height: 32 },
  { name: "Shopify", logo: "/logos/shopify.svg", width: 100, height: 32 },
  { name: "Stripe", logo: "/logos/stripe.svg", width: 80, height: 32 },
  { name: "Square", logo: "/logos/square.svg", width: 90, height: 32 },
  { name: "CommBank", logo: "/logos/commbank.svg", width: 120, height: 32 },
  { name: "PayPal", logo: "/logos/paypal.svg", width: 100, height: 32 },
];

// Dynamic imports
const NewHero = dynamic(
  () => import("@/components/homepages/NewHero").then((mod) => mod.default),
  { ssr: true }
);

const Footer = dynamic(
  () => import("@/components/homepages/Footer").then((mod) => mod.default),
  { ssr: true }
);

const CallToAction = dynamic(
  () =>
    import("@/components/homepages/CallToAction").then(
      (mod) => mod.CallToAction
    ),
  { ssr: true }
);

const AdditionalServiceSection = dynamic(
  () =>
    import("@/components/homepages/AdditionalServiceSection").then(
      (mod) => mod.default
    ),
  { ssr: true }
);

const ClientSection = dynamic(
  () =>
    import("@/components/homepages/ClientSection").then(
      (mod) => mod.ClientSection
    ),
  { ssr: true }
);

const NewSection = dynamic(
  () =>
    import("@/components/homepages/NewSection").then((mod) => mod.NewSection),
  { ssr: true }
);

const PrimarySection = dynamic(
  () =>
    import("@/components/homepages/PrimarySection").then((mod) => mod.default),
  { ssr: true }
);

const SecondarySection = dynamic(
  () =>
    import("@/components/homepages/SecondarySection").then(
      (mod) => mod.SecondarySection
    ),
  { ssr: true }
);

const HowItWorks = dynamic(
  () =>
    import("@/components/solutions/PrimarySections").then(
      (mod) => mod.default
    ),
  { ssr: true }
);

// Testimonial Section Component
const TestimonialSection = () => (
  <section className="py-[120px] px-6 lg:px-10 bg-base-white">
    <div className="max-w-[1340px] mx-auto">
      {/* Horizontal title layout */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-[60px] fade-in">
        <h2 className="font-inter text-[36px] leading-[46px] font-extrabold tracking-[-1.5px]">
          Why Aussie businesses love us
        </h2>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[20px] text-[#F59E0B]">★★★★★</span>
          <span className="text-base font-semibold ml-2">5.0</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
        {testimonials.map((t) => (
          <div key={t.name} className="fade-in bg-blue-primary-50 rounded-[20px] p-9">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3.5">
                {t.image ? (
                  <Image
                    src={t.image}
                    alt={t.name}
                    width={48}
                    height={48}
                    className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-blue-primary-900 flex items-center justify-center text-white font-bold text-base">
                    {t.initials}
                  </div>
                )}
                <div>
                  <div className="font-inter font-bold text-base">
                    {t.name}
                  </div>
                  <div className="text-[13px] text-gray-cool-500">{t.role}</div>
                </div>
              </div>
              {/* Platform logo in square with border */}
              <div className="w-10 h-10 border border-gray-cool-200 rounded-lg flex items-center justify-center flex-shrink-0 bg-white">
                <span className="text-[16px]">{t.platformIcon}</span>
              </div>
            </div>
            <p className="font-inter text-[15px] leading-[1.8] text-gray-cool-700">
              &ldquo;{t.quote}&rdquo;
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  const router = useRouter();
  const pricingRef = useRef(null);
  const featureRef = useRef(null);
  const homeRef = useRef(null);
  const howItWorksRef = useRef(null);
  const teamRef = useRef(null);

  useEffect(() => {
    // Handle hash-based navigation
    if (router.asPath.includes("#")) {
      const hash = router.asPath.split("#")[1];
      if (typeof window === "object") {
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 800);
      }
    } else if (router.asPath === "/") {
      if (typeof window === "object" && homeRef.current != null) {
        window.scrollTo({
          top: homeRef.current.offsetTop,
          left: 0,
          behavior: "smooth",
        });
      }
    }
  }, []);

  // Scroll-triggered fade-in animations
  useEffect(() => {
    if (typeof window === "undefined") return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );

    // Observe all current fade-in elements
    document.querySelectorAll(".fade-in:not(.visible)").forEach((el) => io.observe(el));

    // Watch for dynamically added fade-in elements
    const mo = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.classList?.contains("fade-in") && !node.classList.contains("visible")) {
            io.observe(node);
          }
          node.querySelectorAll?.(".fade-in:not(.visible)").forEach((el) => io.observe(el));
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [router.asPath]);

  return (
    <>
      <Head>
        <title>AI Bookkeeping Australia | Human-Verified Accounting - Ledgerowl</title>
        <meta
          property="og:description"
          content="Australia's AI-powered bookkeeping service with human-verified accuracy. Automated BAS lodgement, real-time reporting & expert accountants. Fixed fee plans from $199/mo."
        />
        <meta
          property="og:title"
          content="AI Bookkeeping Australia | Human-Verified Accounting - Ledgerowl"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SITE_URL} />
        <meta property="og:image" content="https://ledgerowl.com.au/favicon.png" />
        <meta property="og:site_name" content="Ledgerowl" />
        <meta property="og:locale" content="en_AU" />
        <meta
          name="title"
          content="AI Bookkeeping Australia | Human-Verified Accounting - Ledgerowl"
        />
        <meta
          name="description"
          content="Australia's AI-powered bookkeeping service with human-verified accuracy. Automated BAS lodgement, real-time reporting & expert accountants. Fixed fee plans from $199/mo."
        />
        <meta
          name="keywords"
          content="AI bookkeeping Australia, AI accounting service Australia, human verified bookkeeping Australia, automated BAS lodgement Australia, bookkeeping service with accountant Australia, BAS agent Sydney, fixed fee bookkeeping Australia, bookkeeping for startups Sydney, small business bookkeeper Sydney"
        />
        <link rel="canonical" href={SITE_URL} />
        <link rel="icon" href="/favicon.png" />
      </Head>

      <OrganizationSchema />
      <LocalBusinessSchema />

      {/* 1. Nav */}
      <NewHeader
        homeRef={homeRef}
        pricingRef={pricingRef}
        featureRef={featureRef}
        howItWorksRef={howItWorksRef}
        teamRef={teamRef}
      />

      {/* 2. Hero */}
      <NewHero homeRef={homeRef} />

      {/* 3. Backed By */}
      <div className="bg-base-white border-b border-black/[0.04]">
        <div className="max-w-[1340px] mx-auto">
          <ClientSection
            content={backedByLogos}
            title="Backed By"
            compact
            maxWidth={570}
          />
        </div>
      </div>

      {/* 4. AI + Human Statement */}
      <SecondarySection />

      {/* 4b. Trusted By */}
      <div className="bg-base-white border-b border-black/[0.04]">
        <div className="max-w-[1340px] mx-auto">
          <ClientSection
            content={trustedLogos}
            title="Trusted by Leading Brands in Australia & Beyond"
            compact
            narrow
          />
        </div>
      </div>

      {/* 5. How It Works */}
      <HowItWorks />

      {/* 6. Features */}
      <PrimarySection featureRef={featureRef} />

      {/* 7. Meet the Team */}
      <NewSection />

      {/* 8. Why Ledgerowl */}
      <AdditionalServiceSection />

      {/* 9. Testimonials */}
      <TestimonialSection />

      {/* 10. Integrations */}
      <div className="bg-cream-alt">
        <div className="max-w-[1340px] mx-auto py-[100px] px-6 lg:px-10">
          <h2 className="font-inter text-[36px] sm:text-[42px] lg:text-[54px] font-extrabold tracking-[-1.5px] mb-[60px] fade-in">
            We integrate with the{" "}
            <span className="text-blue-primary-500">best platforms</span>
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-x-16 gap-y-8 max-w-6xl mx-auto px-8 fade-in">
            {integrationLogos.map((item) => (
              <Image
                key={item.name}
                src={item.logo}
                alt={item.name}
                width={item.width}
                height={item.height}
                className="h-[36px] w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-200"
              />
            ))}
          </div>
          <div className="flex justify-center mt-8 fade-in">
            <Link
              href="/contact"
              className="text-blue-primary-500 font-semibold text-base inline-flex items-center gap-1.5 hover:gap-2.5 transition-all duration-200"
            >
              See All Integrations
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* 11. CTA */}
      <CallToAction />

      {/* 12. Footer */}
      <Footer />
    </>
  );
}
